import { IcoCheckSmall16 } from '@onefootprint/icons';
import * as RadixSelect from '@radix-ui/react-select';
import { cx } from 'class-variance-authority';
import { forwardRef, isValidElement } from 'react';

type Size = 'default' | 'compact' | 'tiny';

type ItemProps = RadixSelect.SelectItemProps & {
  size?: Size;
  showChecked?: boolean;
  value: string;
  description?: string;
};

const CustomItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ children, size = 'default', showChecked, value, description, disabled, ...props }, ref) => {
    const isComponent = isValidElement(children);
    const hasDescription = !!description;

    return (
      <RadixSelect.Item value={value} disabled={disabled} asChild>
        <div
          ref={ref}
          translate="no"
          className={cx(
            'flex w-full relative cursor-pointer transition-colors duration-50 ease-in-out overflow-hidden rounded-sm gap-2',
            'text-body-2 text-primary hover:bg-(--dropdown-hover-bg) focus-visible:not:hover:outline focus-visible:not:hover:outline-2 focus-visible:not:hover:outline-accent focus-visible:not:hover:outline-offset-1',
            'data-[highlighted]:bg-(--dropdown-hover-bg) data-[highlighted]:outline-none data-[disabled]:text-quaternary data-[disabled]:cursor-default data-[disabled]:hover:bg-transparent',
            {
              // For string labels: fixed height with centered items
              'items-center pl-3 pr-4 h-9': size === 'default' && !isComponent && !hasDescription,
              'items-center pl-3 pr-4 h-7': (size === 'compact' || size === 'tiny') && !isComponent && !hasDescription,
              // For React components or items with description: allow flexible height with top alignment
              'items-start pl-3 pr-4 py-2.5': isComponent || hasDescription,
            },
          )}
          {...props}
        >
          <RadixSelect.ItemText
            className={cx('flex-1 min-w-0', {
              'text-body-2 overflow-hidden truncate whitespace-nowrap text-ellipsis': !isComponent && !hasDescription,
              'overflow-visible flex flex-col': isComponent || hasDescription,
            })}
          >
            {isComponent ? (
              children
            ) : hasDescription ? (
              <div className="flex flex-col">
                <span className="text-body-2 font-medium">{children}</span>
                <span className="text-body-2 text-tertiary">{description}</span>
              </div>
            ) : (
              <span className="w-full line-clamp-1 text-body-2">{children}</span>
            )}
          </RadixSelect.ItemText>
          {showChecked && (
            <RadixSelect.ItemIndicator className="flex flex-shrink-0 justify-center items-center ml-auto">
              <IcoCheckSmall16 />
            </RadixSelect.ItemIndicator>
          )}
        </div>
      </RadixSelect.Item>
    );
  },
);

export default CustomItem;
